import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import {Image, TouchableOpacity} from "react-native";
import MainStyle from "../../../MainStyle";
import Input from "../Input";
import Button from "../Button";
import {useFormik} from "formik";
import {StoreContext} from "../StoreContextProvider";
import {isFullSwing} from "../../constants";
import {colors} from "../../assets/styles/colors";
import ServiceClient from "../../utils/ServiceClient";
import KycTerm from "../../screens/Auth/SignUp/steps/KycTerm";
import Div from "../../lib/Div";
import questionsIcn from '../../assets/img/questions-icn.png'
import BonusCashInfoModal from "../BonusCashInfoModal";
import {formatCurrency, getFieldError} from "../../utils";
import P from "../../lib/P";
import LoadingKYC from "../LoadingKYC";
import FontAwesome5 from '@expo/vector-icons/FontAwesome5';
import Btn from "../Btn";
import {HelperText, TextInput} from "react-native-paper";
import AllPromosModal from "../../modals/AllPromosModal";
import CompleteKYC from "../../modals/CompleteKYC";
import WithdrawFlowModal from "../../modals/WithdrawFlowModal";
import {
  createWithdrawAmountValidationSchema, getAvailableWithdrawalCents, getDefaultWithdrawAmount
} from "./validation";

type Props = { changeTab: (val: number) => void };

const Withdraw = (props: Props) => {
  const Context = useContext(StoreContext);
  const {cancelModalConfig, balance, user, totalBonuses} = Context;
  const hasUserEditedAmountRef = useRef(false);
  const availableWithdrawalCents = useMemo(() => {
    return getAvailableWithdrawalCents(balance, totalBonuses);
  }, [balance, totalBonuses]);
  const validationSchema = useMemo(() => {
    return createWithdrawAmountValidationSchema(availableWithdrawalCents);
  }, [availableWithdrawalCents]);
  const formik = useFormik({
    validationSchema, initialValues: {amount: '50'},
    onSubmit: values => {
      if (!user?.emailConfirmed) {
        Context.setCancelModalConfig({
          visible: true, title: 'VERIFY YOUR EMAIL', subtitle: 'To withdraw funds, you must verify your email.',
          leftBtnText: 'RESEND VERIFICATION EMAIL', leftBtnBackgroundColor: colors.unselect, leftBtnFontSize: 20,
          rightBtnText: 'OK', rightBtnFontSize: 20,
          onExit: () => {
            Context.setCancelModalConfig({...cancelModalConfig, visible: false});

            ServiceClient.resendEmailConfirmation().then(() => {
              console.log('resendEmailConfirmation');
              Context.setIsSuccessMessageShow('Email Confirmation Sent\n\nIf you do not see email, check your “junk” or “spam” folder');

              setTimeout(() => {
                Context.setIsSuccessMessageShow('');
              }, 3000);

            }).catch(err => {
              console.log('resendEmailConfirmation err', err);

              Context.setCancelModalConfig({
                visible: true, title: 'ERROR', leftBtnText: 'OK', subtitle: err.message,
                onExit: () => {
                  Context.setCancelModalConfig({...cancelModalConfig, visible: false});
                }
              });
            });
          },
          onResume: () => {
            Context.setCancelModalConfig({...cancelModalConfig, visible: false});
            props.changeTab(0);
          }
        });

      } else {
        setConfirmAmount(values.amount);
      }
    }
  });
  const amountError = getFieldError('amount', formik);
  const [showOnce, setShowOnce] = useState(false);
  const [isOpenBonusInfoModal, setIsOpenBonusInfoModal] = useState(false)
  const kycStatus = user?.kycStatus;
  // const kycStatus = 1 as any as number;
  const [confirmAmount, setConfirmAmount] = useState('');
  const [showAllPromos, setShowAllPromos] = useState(false);
  const [showWarning, setShowWarning] = useState(false);

  const restartKyc = () => {
    if (!user) return;
    Context.setUser({...user, kycStatus: 0});
  };

  useEffect(() => {
    if ((kycStatus === 0 || kycStatus === 2) && !showOnce) {
      setShowOnce(true);

      if (isFullSwing) {
        setShowWarning(true);
      } else {
        Context.setCancelModalConfig({
          visible: true, title: 'Complete KYC', titleColor: MainStyle.c_yellow, rightBtnText: 'COMPLETE KYC',
          subtitle: 'We will need to verify your identity to withdraw or deposit.',
          onClose: () => Context.setCancelModalConfig({...cancelModalConfig, visible: false}),
          onResume: () => Context.setCancelModalConfig({...cancelModalConfig, visible: false})
        });
      }
    }
  }, [kycStatus, showOnce]);

  useEffect(() => {
    if (hasUserEditedAmountRef.current) return;

    formik.setFieldValue('amount', getDefaultWithdrawAmount(availableWithdrawalCents));
  }, [availableWithdrawalCents]);

  const setAmount = (amount: string) => {
    hasUserEditedAmountRef.current = true;
    formik.setFieldValue('amount', amount);
  };

  const bonusAmount = (balance: number | bigint, availableToWithdraw: number | bigint) => {
    return Number(balance) - Number(availableToWithdraw) ? formatCurrency((Number(balance) - Number(availableToWithdraw)) / 100) : '$0';
  };

  return (
    <>
      {kycStatus === 0 || kycStatus === 2 ? (
        <KycTerm redirectUser={Context.getUser}/>
      ) : kycStatus === 3 || kycStatus === 4 ? (
        <LoadingKYC kycStatus={kycStatus} restartKyc={restartKyc}/>
      ) : (
        <Div mt={30} px={isFullSwing ? 15 : 0} flex={1}>
          {!isFullSwing && <P ta="center" fs={18} fw="500" mb={5}>BALANCE</P>}

          <Div w={isFullSwing ? '100%' : 240} rounded={isFullSwing ? 12 : 20} borderWidth={1} alignItems="center" row
               borderColor={isFullSwing ? '#494A4C' : colors.settings_border} mx="auto" mb={isFullSwing ? 20 : 15}
               px={isFullSwing ? 20 : 0} py={isFullSwing ? 16 : 0}
               justifyContent={isFullSwing ? 'space-between' : 'center'}>
            {isFullSwing && <P fontFamily="DavisSans-Regular" lh={30}>Balance</P>}

            <P fs={isFullSwing ? 20 : 32} fw={isFullSwing ? undefined : '500'} lh={isFullSwing ? 30 : 60}
               color={isFullSwing ? 'white' : colors.balance} fontFamily={isFullSwing ? 'DavisSans-Medium' : undefined}>
              {balance ? formatCurrency(Number(balance) / 100) : '$0'}
            </P>
          </Div>

          {!isFullSwing && (
            <Div mb={5} w={240} mx="auto" position="relative">
              <Div position="absolute" left={9} top={-2}>
                <TouchableOpacity onPress={() => setIsOpenBonusInfoModal(true)}>
                  <Image source={questionsIcn} style={{width: 24, height: 24, alignSelf: 'center'}}/>
                </TouchableOpacity>
              </Div>

              <P ta="center" fs={18} fw="500">BONUS CASH</P>
            </Div>
          )}

          <Div w={isFullSwing ? '100%' : 240} rounded={isFullSwing ? 12 : 20} borderWidth={1} alignItems="center" row
               borderColor={isFullSwing ? '#494A4C' : colors.settings_border} mx="auto" mb={isFullSwing ? 20 : 15}
               px={isFullSwing ? 20 : 0} py={isFullSwing ? 16 : 0}
               justifyContent={isFullSwing ? 'space-between' : 'center'}>
            <Div row alignItems="center">
              {isFullSwing && <P fontFamily="DavisSans-Regular" lh={30}>Bonus Cash</P>}

              <Btn ml={2} w={28} h={28} alignItems="center" justifyContent="center"
                   onPress={() => setIsOpenBonusInfoModal(true)}>
                <FontAwesome5 name="question-circle" size={20} color="#76777A"/>
              </Btn>
            </Div>

            <P fs={isFullSwing ? 20 : 32} fw={isFullSwing ? undefined : '500'} lh={isFullSwing ? 30 : 60}
               color={isFullSwing ? 'white' : colors.yellow} fontFamily={isFullSwing ? 'DavisSans-Medium' : undefined}>
              {bonusAmount(balance, balance - totalBonuses)}
            </P>
          </Div>

          {!isFullSwing && (
            <P ta="center" fs={18} fw="500" mb={5}>AVAILABLE TO WITHDRAW</P>
          )}

          <Div w={isFullSwing ? '100%' : 240} rounded={isFullSwing ? 12 : 20} borderWidth={1} alignItems="center" row
               borderColor={isFullSwing ? '#494A4C' : colors.settings_border} mx="auto" mb={isFullSwing ? 60 : 29}
               px={isFullSwing ? 20 : 0} py={isFullSwing ? 16 : 0}
               justifyContent={isFullSwing ? 'space-between' : 'center'}>
            {isFullSwing && <P fontFamily="DavisSans-Regular" lh={30}>Available to Withdraw</P>}

            <P fs={isFullSwing ? 20 : 32} fw={isFullSwing ? undefined : '500'} lh={isFullSwing ? 30 : 60}
               color={isFullSwing ? '#2DFF1A' : 'white'} fontFamily={isFullSwing ? 'DavisSans-Medium' : undefined}>
              {(balance - totalBonuses) ? formatCurrency(Number(balance - totalBonuses) / 100) : '$0'}
            </P>
          </Div>

          {isFullSwing ? (
            <>
              <P fontFamily="DavisSans-Regular" lh={24} ta="center" mb={20}>
                Enter the amount you would like to withdraw:
              </P>

              <TextInput
                label="Amount to Withdraw" onBlur={formik.handleBlur('amount')} value={formik.values.amount}
                style={[MainStyle.b_r_4, MainStyle.m_b_10]} error={!!amountError} keyboardType="numeric"
                // String icon names resolve through react-native-vector-icons, which this
                // app does not bundle (renders a placeholder box); use an explicit glyph.
                onChangeText={setAmount} underlineColor="transparent"
                left={<TextInput.Icon icon={() => <FontAwesome5 name="dollar-sign" size={18} color="white"/>}/>}
              />

              {!!amountError && (
                <HelperText type="error" visible={true} style={{margin: 0, padding: 0, paddingLeft: 4}} padding="none">
                  {amountError as string}
                </HelperText>
              )}

              <Div flex={1}/>

              <Btn bg="#3369FF" rounded={4} mb={20} mt={16} onPress={() => formik.handleSubmit()}>
                <P lh={56} ta="center" fontFamily="DavisSans-Medium">Withdraw</P>
              </Btn>

              <Btn mb={18} onPress={() => setShowAllPromos(true)}>
                <P lh={56} ta="center" fontFamily="DavisSans-Regular" color="white" textDecorationLine="underline">
                  See All Promos
                </P>
              </Btn>
            </>
          ) : (
            <Div w={330} mx="auto" pt={24} alignItems="center" bg={colors.new_input_color} rounded={20} borderWidth={1}
                 borderColor={colors.settings_border} mb={10}>
              <P ta="center" fs={18} fw="500" mb={5}>AMOUNT IN $</P>

              <Input
                type="numeric" placeholder="AMOUNT" dollarMask useGrouping decimalPlaces={2} w={240} h={60} rounded={20}
                borderColor={colors.settings_border} value={formik.values.amount} fs={32}
                onChangeText={setAmount} textAlign="center" onBlur={formik.handleBlur('amount')}
                error={amountError} color={colors.newGreenText} mb={30}
              />

              <Button text="WITHDRAW" w={240} mb={36} mx="auto" onPress={() => formik.handleSubmit()}/>
            </Div>
          )}
        </Div>
      )}

      <BonusCashInfoModal
        isOpen={isOpenBonusInfoModal}
        onClose={() => setIsOpenBonusInfoModal(false)}
      />

      <WithdrawFlowModal amount={confirmAmount} onClose={() => setConfirmAmount('')}/>

      <AllPromosModal visible={showAllPromos} onClose={() => setShowAllPromos(false)}/>
      <CompleteKYC visible={showWarning} onClose={() => setShowWarning(false)}/>
    </>
  );
};

export default React.memo(Withdraw);
